import React, { lazy, Suspense } from 'react';
import {
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import i18next from 'i18next';
import dayjs from 'utils/day';
import queryString from 'query-string';

import requiresAuth from 'utils/hoc/requiresAuth';
import requiresPlan from 'utils/hoc/requiresPlan';
import withBaseElements from 'utils/hoc/withBaseElements';
import PageLoadingFallback from 'components/page-loading-fallback';
import { useUser, useToken, useUpdateUser } from 'utils/contexts/app-context';
import ChangePasswordDialog from 'components/ChangePasswordDialog';
import MfaMethodSelectionDialog from 'containers/profile/mfa-method-selection-dialog';
import SmsSetupDialog from 'containers/profile/mfa-sms-setup-dialog';
import TotpSetupDialog from 'containers/profile/mfa-totp-setup-dialog';
import axios from 'axios';
import { AUTH, PUBLIC, PRIVATE, ADMIN } from './routes';

const Login = lazy(() => import('containers/login'));
const Register = lazy(() => import('containers/register'));
const CognitoOAuthPopup = lazy(() => import('containers/auth/CognitoOAuthPopup'));
const Activate = lazy(() => import('containers/activate'));
const ResetPassword = lazy(() => import('containers/reset-password'));
const MagicLinkVerify = lazy(() => import('containers/login/MagicLinkVerify/MagicLinkVerify'));
const MfaVerify = lazy(() => import('containers/mfa-verify'));

const Profile = lazy(() => import('containers/profile'));

const Company = lazy(() => import('containers/company'));

const Terms = lazy(() => import('containers/terms'));
const Privacy = lazy(() => import('containers/privacy'));

const ValidationsNew = lazy(() => import('../pages/validations'));
const TeamDetails = lazy(() => import('../pages/teams/TeamDetails'));
const Teams = lazy(() => import('../pages/teams'));
const Users = lazy(() => import('../pages/users'));

const IvReport = lazy(() => import('../pages/individualReport'));
const LEVReport = lazy(() => import('../pages/levReport'));

const switchLanguage = (location, navigate) => {
  if (!location) return;

  const params = queryString.parse(location.search);
  if (
    params.language &&
    i18next.language &&
    !i18next.language.includes(params.language)
  ) {
    i18next.changeLanguage(params.language);
    dayjs.locale(params.language);
    navigate(`${location.pathname}${location.search}`, { replace: true });
  }
};

const AuthRouter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  switchLanguage(location, navigate);

  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        <Route path={AUTH.magic_link} element={<MagicLinkVerify />} />
        <Route path={AUTH.mfaVerify} element={<MfaVerify />} />
        <Route path={AUTH.cognitoPopup} element={<CognitoOAuthPopup />} />
        <Route path={AUTH.register} element={<Register />} />
        <Route path={AUTH.login} element={<Login />} />
        <Route path={AUTH.activate} element={<Activate />} />
        <Route path={AUTH.reset_password} element={<ResetPassword />} />
        <Route path="*" element={<Navigate to={AUTH.login} replace />} />
      </Routes>
    </Suspense>
  );
};

const PrivateRouterInner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  switchLanguage(location, navigate);
  const user = useUser();
  const token = useToken();
  const updateUser = useUpdateUser();
  const [showChangePasswordDialog, setShowChangePasswordDialog] = React.useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = React.useState(false);
  const [mfaSetupDialogOpen, setMfaSetupDialogOpen] = React.useState(false);
  const [smsSetupDialogOpen, setSmsSetupDialogOpen] = React.useState(false);
  const [totpSetupDialogOpen, setTotpSetupDialogOpen] = React.useState(false);

  React.useEffect(() => {
    if (user && user.passwordSetByAdmin && user?.company?.authenticate_mode === 'Default') {
      setShowChangePasswordDialog(true);
    }
  }, [user]);

  const currentPath =
    (location && location.pathname) ||
    (typeof window !== 'undefined' ? window.location.pathname : '');
  const isOnProfilePage =
    typeof currentPath === 'string' &&
    (currentPath.indexOf(PRIVATE.profile) === 0 ||
      currentPath === PRIVATE.profile ||
      currentPath === PRIVATE.profileSecurity ||
      currentPath === PRIVATE.profileValidations ||
      currentPath.includes('/profile'));

  const isAdmin = user?.privilegeLevel === 'ADMIN' || user?.accessLevel === 'ADMIN';
  const isOwner = user?.isOwner || user?.privilegeLevel === 'OWNER' || user?.accessLevel === 'OWNER';
  const canDisableCompanyMfa = isAdmin || isOwner;

  React.useEffect(() => {
    if (user && user.needsMfaSetup && !isOnProfilePage) {
      setMfaSetupRequired(true);
      setMfaSetupDialogOpen(true);
    } else {
      setMfaSetupRequired(false);
      setMfaSetupDialogOpen(false);
    }
  }, [user, isOnProfilePage]);

  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        <Route path="profile" element={<Profile />} />
        <Route path="profile/security" element={<Profile />} />
        <Route path="profile/validations" element={<Profile />} />

        <Route path="validations/*" element={<ValidationsNew />} />
        <Route path="iv-report/:uuid" element={<IvReport />} />
        <Route path="lev-report/:uuid" element={<LEVReport />} />
        <Route path="report/:uuid" element={<IvReport />} />
        <Route path="*" element={<Navigate to="validations" replace />} />
      </Routes>

      <ChangePasswordDialog
        open={showChangePasswordDialog}
        onClose={() => setShowChangePasswordDialog(false)}
      />

      {mfaSetupRequired && (
        <>
          <MfaMethodSelectionDialog
            open={mfaSetupDialogOpen}
            onClose={async () => {
              if (canDisableCompanyMfa) {
                try {
                  const SERVER_URL = `${process.env.REACT_APP_NODE_SERVER_URL || ''}/app`;
                  const response = await axios.post(
                    `${SERVER_URL}/auth/mfa/company/disable`,
                    {},
                    {
                      headers: { Authorization: `Bearer ${token}` }
                    }
                  );

                  if (response.status === 200 || response.data?.success) {
                    setMfaSetupRequired(false);
                    setMfaSetupDialogOpen(false);
                    if (token) {
                      await updateUser(true);
                    }
                  }
                } catch (err) {
                  console.error('[ROUTER] Error disabling company MFA on cancel:', err);
                  setMfaSetupDialogOpen(false);
                }
              }
            }}
            onSelectMethod={(method) => {
              setMfaSetupDialogOpen(false);
              if (method === 'SMS') {
                setSmsSetupDialogOpen(true);
              } else if (method === 'TOTP') {
                setTotpSetupDialogOpen(true);
              }
            }}
            canDisableCompanyMfa={canDisableCompanyMfa}
            onDisableCompanyMfa={async () => {
              try {
                const SERVER_URL = `${process.env.REACT_APP_NODE_SERVER_URL || ''}/app`;
                const response = await axios.post(
                  `${SERVER_URL}/auth/mfa/company/disable`,
                  {},
                  {
                    headers: { Authorization: `Bearer ${token}` }
                  }
                );

                if (response.data.success) {
                  setMfaSetupRequired(false);
                  setMfaSetupDialogOpen(false);
                  if (token) {
                    await updateUser(true);
                  }
                }
              } catch (err) {
                console.error('[ROUTER] Error disabling company MFA:', err);
              }
            }}
          />
          <SmsSetupDialog
            open={smsSetupDialogOpen}
            onClose={() => {
              setSmsSetupDialogOpen(false);
              setMfaSetupDialogOpen(true);
            }}
            onSuccess={async () => {
              setSmsSetupDialogOpen(false);
              setMfaSetupRequired(false);
              if (token) {
                await updateUser(true);
              }
            }}
          />
          <TotpSetupDialog
            open={totpSetupDialogOpen}
            onClose={() => {
              setTotpSetupDialogOpen(false);
              setMfaSetupDialogOpen(true);
            }}
            onSuccess={async () => {
              setTotpSetupDialogOpen(false);
              setMfaSetupRequired(false);
              if (token) {
                await updateUser(true);
              }
            }}
          />
        </>
      )}
    </Suspense>
  );
};

const PrivateRouter = withBaseElements(PrivateRouterInner);

const AdminRouterInner = () => {
  const user = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  switchLanguage(location, navigate);

  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        {(user.accessLevel === 'OWNER' || user.accessLevel === 'ADMIN') && (
          <Route path="users/*" element={<Users />} />
        )}

        {(user.accessLevel === 'OWNER' || user.accessLevel === 'ADMIN') && (
          <Route path="teams/*" element={<Teams />} />
        )}

        <Route path="team/:tid/*" element={<TeamDetails />} />
        <Route path="company/*" element={<Company />} />
      </Routes>
    </Suspense>
  );
};

const AdminRouter = withBaseElements(AdminRouterInner);

const WebsiteRedirect = () => {
  const websiteUrl = 'https://www.valital.com';
  React.useLayoutEffect(() => {
    window.location.href =
      i18next.language === 'fr' ? `${websiteUrl}/fr` : websiteUrl;
  }, []);
  return null;
};

const PublicRouter = () => {
  return <WebsiteRedirect />;
};

export default {
  AuthRouter,
  PublicRouter,
  PrivateRouter: requiresAuth(requiresPlan(PrivateRouter)),
  AdminRouter: requiresAuth(requiresPlan(AdminRouter, false), {
    authType: 'ADMIN',
    redirectTo: PRIVATE.dashboard,
  }),
};
